import { NOTE_CATEGORIES, NOTE_CATEGORY_LABELS, VALID_NOTE_CATEGORIES } from "./notes";

export const NOTES_PREFERENCES_STORAGE_KEY = "vocabularium_notes_preferences";

export const NOTES_LIST_DISPLAY_MODES = {
  CREATED_AT: "createdAt",
  UPDATED_AT: "updatedAt",
  SIZE: "size",
  CATEGORY: "category",
};

export const NOTES_SORT_ORDERS = {
  ALPHABETICAL: "alphabetical",
  UPDATED_AT: "updatedAt",
  CREATED_AT: "createdAt",
  SIZE: "size",
};

export const NOTES_SCROLL_ANCHORS = {
  FREE: "free",
  PAGE_END: "pageEnd",
};

export const NOTES_CATEGORY_FILTER_ALL = "all";

export const DEFAULT_NOTES_PREFERENCES = {
  listDisplayMode: NOTES_LIST_DISPLAY_MODES.UPDATED_AT,
  sortOrder: NOTES_SORT_ORDERS.UPDATED_AT,
  scrollAnchor: NOTES_SCROLL_ANCHORS.FREE,
  categoryFilter: NOTES_CATEGORY_FILTER_ALL,
};

export const NOTES_LIST_DISPLAY_LABELS = {
  [NOTES_LIST_DISPLAY_MODES.CREATED_AT]: "Date de création",
  [NOTES_LIST_DISPLAY_MODES.UPDATED_AT]: "Date de modification",
  [NOTES_LIST_DISPLAY_MODES.SIZE]: "Taille",
  [NOTES_LIST_DISPLAY_MODES.CATEGORY]: "Rubrique",
};

export const NOTES_LIST_DISPLAY_HELP = {
  [NOTES_LIST_DISPLAY_MODES.CREATED_AT]:
    "Date de création de la note, affichée à droite du titre.",
  [NOTES_LIST_DISPLAY_MODES.UPDATED_AT]:
    "Date de la dernière modification, affichée à droite du titre.",
  [NOTES_LIST_DISPLAY_MODES.SIZE]:
    "Taille du contenu de la note (octets / Ko / Mo), affichée à droite du titre.",
  [NOTES_LIST_DISPLAY_MODES.CATEGORY]:
    "Rubrique de la note, indiquée par une pastille de couleur à droite du titre.",
};

export const NOTES_SORT_ORDER_LABELS = {
  [NOTES_SORT_ORDERS.ALPHABETICAL]: "Ordre alphabétique",
  [NOTES_SORT_ORDERS.UPDATED_AT]: "Date de modification",
  [NOTES_SORT_ORDERS.CREATED_AT]: "Date de création",
  [NOTES_SORT_ORDERS.SIZE]: "Taille",
};

export const NOTES_SORT_ORDER_HELP = {
  [NOTES_SORT_ORDERS.ALPHABETICAL]:
    "Trie les notes de A à Z selon leur titre.",
  [NOTES_SORT_ORDERS.UPDATED_AT]:
    "Trie les notes de la plus récemment modifiée à la plus ancienne.",
  [NOTES_SORT_ORDERS.CREATED_AT]:
    "Trie les notes de la plus récemment créée à la plus ancienne.",
  [NOTES_SORT_ORDERS.SIZE]:
    "Trie les notes de la plus volumineuse à la plus petite.",
};

export const NOTES_SCROLL_ANCHOR_LABELS = {
  [NOTES_SCROLL_ANCHORS.FREE]: "Scroll libre",
  [NOTES_SCROLL_ANCHORS.PAGE_END]: "Fin de page",
};

/** Couleurs de pastille pour l'affichage « rubrique » (sans texte). */
export const NOTES_CATEGORY_BADGE_TONES = {
  [NOTE_CATEGORIES.REPERES_TEXTE]: "blue",
  [NOTE_CATEGORIES.REPERES_HISTORIQUES]: "violet",
  [NOTE_CATEGORIES.FAITS_INTERESSANTS]: "orange",
  [NOTE_CATEGORIES.ESSAIS_LITTERAIRES]: "green",
  [NOTE_CATEGORIES.AUTRE]: "red",
};

export const NOTES_CATEGORY_FILTER_OPTIONS = [
  { value: NOTES_CATEGORY_FILTER_ALL, label: "Toutes les notes" },
  ...Object.values(NOTE_CATEGORIES).map((value) => ({
    value,
    label: NOTE_CATEGORY_LABELS[value],
  })),
];

const VALID_LIST_DISPLAY_MODES = new Set(Object.values(NOTES_LIST_DISPLAY_MODES));
const VALID_SORT_ORDERS = new Set(Object.values(NOTES_SORT_ORDERS));
const VALID_SCROLL_ANCHORS = new Set(Object.values(NOTES_SCROLL_ANCHORS));

export function sanitizeNotesCategoryFilter(raw) {
  const value = String(raw ?? "").trim();
  if (value === NOTES_CATEGORY_FILTER_ALL) return NOTES_CATEGORY_FILTER_ALL;
  return VALID_NOTE_CATEGORIES.has(value) ? value : NOTES_CATEGORY_FILTER_ALL;
}

export function sanitizeNotesPreferences(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_NOTES_PREFERENCES };
  }

  return {
    listDisplayMode: VALID_LIST_DISPLAY_MODES.has(raw.listDisplayMode)
      ? raw.listDisplayMode
      : DEFAULT_NOTES_PREFERENCES.listDisplayMode,
    sortOrder: VALID_SORT_ORDERS.has(raw.sortOrder)
      ? raw.sortOrder
      : DEFAULT_NOTES_PREFERENCES.sortOrder,
    scrollAnchor: VALID_SCROLL_ANCHORS.has(raw.scrollAnchor)
      ? raw.scrollAnchor
      : DEFAULT_NOTES_PREFERENCES.scrollAnchor,
    categoryFilter: sanitizeNotesCategoryFilter(raw.categoryFilter),
  };
}

export function readNotesPreferences() {
  try {
    const stored = localStorage.getItem(NOTES_PREFERENCES_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_NOTES_PREFERENCES };
    return sanitizeNotesPreferences(JSON.parse(stored));
  } catch {
    return { ...DEFAULT_NOTES_PREFERENCES };
  }
}

export function writeNotesPreferences(preferences) {
  try {
    localStorage.setItem(
      NOTES_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizeNotesPreferences(preferences))
    );
  } catch {
    // ignore quota errors
  }
}

export function formatNoteDateShort(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

export function formatNoteSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value < 1024) return `${Math.round(value)} o`;
  if (value < 1024 * 1024) {
    const ko = value / 1024;
    const text = ko < 10 ? ko.toFixed(1) : String(Math.round(ko));
    return `${text.replace(".", ",")} Ko`;
  }
  const mo = value / (1024 * 1024);
  const text = mo < 10 ? mo.toFixed(1) : String(Math.round(mo));
  return `${text.replace(".", ",")} Mo`;
}

export function getNoteContentBytes(content) {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(String(content ?? "")).length;
  }
  return unescape(encodeURIComponent(String(content ?? ""))).length;
}

export function normalizeNoteTitleForSort(title) {
  return String(title || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "'")
    .toLocaleLowerCase("fr-FR")
    .trim();
}

export function sortNotesList(list, sortOrder) {
  const items = Array.isArray(list) ? [...list] : [];
  if (sortOrder === NOTES_SORT_ORDERS.ALPHABETICAL) {
    items.sort((a, b) =>
      normalizeNoteTitleForSort(a?.title).localeCompare(
        normalizeNoteTitleForSort(b?.title),
        "fr",
        { sensitivity: "base" }
      )
    );
    return items;
  }

  if (sortOrder === NOTES_SORT_ORDERS.CREATED_AT) {
    items.sort((a, b) =>
      String(b?.createdAt || "").localeCompare(String(a?.createdAt || ""))
    );
    return items;
  }

  if (sortOrder === NOTES_SORT_ORDERS.SIZE) {
    items.sort((a, b) => {
      const sizeA = Number(a?.contentBytes);
      const sizeB = Number(b?.contentBytes);
      const safeA = Number.isFinite(sizeA) ? sizeA : 0;
      const safeB = Number.isFinite(sizeB) ? sizeB : 0;
      return (
        safeB - safeA ||
        String(b?.updatedAt || "").localeCompare(String(a?.updatedAt || ""))
      );
    });
    return items;
  }

  items.sort((a, b) =>
    String(b?.updatedAt || "").localeCompare(String(a?.updatedAt || ""))
  );
  return items;
}
